import { type Brand, type Car } from '@prisma/client'
import React from 'react'
import { getImgSrc } from '#app/shared/lib/misc.js'
import { formatDiscountedPrice, formatPrice } from '#app/shared/lib/price.ts'
import { Separator } from '#app/shared/ui/data-display/Separator.js'
import { Spacer } from '#app/shared/ui/data-display/Spacer.js'
import CarLink from '../../navigation/CarLink'
import CarPriceCardBottom from './CarPriceCardBottom'
import CarSpecialFeatures from './CarSpecialFeatures'

type Props = {
  car: Car & {
    brand: Brand
    images: { id: string }[]
    reservation?: { id: string; isPurchaseCompleted?: boolean } | null
  }
  inCochesUsados?: boolean
}

export default function CarPriceCard({ car, inCochesUsados = false }: Props) {
  const formattedPrice = formatPrice(car.price)

  const formattedDiscountedPrice = formatDiscountedPrice(
    car.price,
    car.discount,
  )

  const features = [
    `${car.matriculation ? new Date(car.matriculation).getFullYear() : car.year}`,
    `${car.mileage.toLocaleString('es-ES')} km`,
    `${car.horsePower} cv`,
    car.transmission,
    car.fuelType,
  ]

  return (
    <div
      className={`
        hover:shadow-xl

        ${
          inCochesUsados
            ? `
              w-full min-w-[290px]

              lg:w-[320px]

              sm:w-[calc(50%-8px)]
            `
            : `
              w-[296px]

              sm:w-[320px]
            `
        }
      `}
    >
      <CarLink carSlug={car.slug}>
        <div
          className={`
            relative aspect-[4/3] overflow-hidden rounded-t-lg

            ${
              inCochesUsados
                ? `w-full`
                : `
                  w-[296px]

                  sm:w-[320px]
                `
            }
          `}
        >
          <img
            className="h-full w-full object-cover object-center"
            src={getImgSrc(car.images[0]?.id, 'carImage')}
            alt={car.model}
            loading="lazy"
          />
          {car.reservation && (
            <>
              <div
                className={`
                  absolute left-3 top-3 z-10 rounded-md bg-primary-500 px-3 py-1
                  text-sm font-semibold text-white shadow-sm
                `}
              >
                {car.reservation.isPurchaseCompleted ? 'Vendido' : 'Reservado'}
              </div>
              <div
                className={`
                  absolute bottom-0 left-0 right-0 top-0 z-0 rounded-t-lg

                  ${
                    car.reservation.isPurchaseCompleted
                      ? 'bg-soldOverlay'
                      : 'bg-reservedOverlay'
                  }
                `}
              ></div>
            </>
          )}
        </div>
        <div
          className={`
            flex h-[220px] flex-col rounded-b-lg border border-neutral-75
            bg-white

            sm:h-[220px]
          `}
        >
          <div
            className={`
              flex flex-1 flex-col px-4

              sm:px-6
            `}
          >
            <Spacer size="20px" />
            <div
              className={`
                flex flex-col justify-between gap-2 overflow-hidden
                whitespace-nowrap
              `}
            >
              <p
                className={`
                  left-0 overflow-hidden text-ellipsis whitespace-nowrap
                  text-h5-bold text-neutral-900
                `}
              >
                {car.brand.name} {car.model}
              </p>
              <p
                className={`
                  w-full overflow-hidden text-ellipsis whitespace-nowrap
                  text-subtitle1-medium text-neutral-700
                `}
              >
                {car.description}
              </p>
            </div>
            <Spacer size="12px" />
            <CarSpecialFeatures features={features} />
            <Spacer size="12px" />
            <div className="mt-auto flex h-[14px] items-center gap-2">
              {(car?.discount ?? 0) > 0 ? (
                <>
                  <p className="text-h5-bold leading-[14px] text-primary-500">
                    {formattedDiscountedPrice}
                  </p>
                  <p
                    className={`
                      text-[20px] font-bold leading-[14px] text-neutral-500
                      line-through
                    `}
                  >
                    {formattedPrice}
                  </p>
                </>
              ) : (
                <p className="text-h5-bold leading-[14px] text-primary-500">
                  {formattedPrice}
                </p>
              )}
            </div>
            <Spacer size="16px" />
          </div>
          <div>
            <Separator className={`w-full bg-neutral-75`} />
            <Spacer size="8px" />
            <CarPriceCardBottom car={car} />
            <Spacer size="12px" />
          </div>
        </div>
      </CarLink>
    </div>
  )
}
